import {
  Button,
  Box,
  Typography,
  Breadcrumbs,
  Card,
  TextField,
  Divider,
  IconButton
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import { Wrapper } from "./styles";
import JobDetailProgress from "../JobDetailProgress/JobDetailProgress";
import ContentLayout from "../ContentLayout/ContentLayout";
import variableStyles from "../../../../../theme/variable-styles";
import { Avatar, Modal } from "../../../../../components";
import {
  createSearchParams,
  useNavigate,
  useNavigation,
  useSearchParams
} from "react-router-dom";
import StageProgress from "../StageProgress";
import TaskService from "../../../../../services/api/task.service";
import { IStageHistory, ITask } from "../../../../../models/Task";
import { ROUTE_PATH } from "../../../../../common/constants/app.constant";
import useNavigateSearch from "../../../../../components/NavigateSearch";
import { toggleMessage } from "../../../../../components/Toast/Toast";
import useAxios from "../../../../../components/UseAxios/useAxios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import CreateTaskModal from "../../../CreateTaskModal/create_task_modal";
import {
  EStageType,
  ETaskStatus
} from "../../../../../models/common/models.enum";
import CommentTask from "../CommentTask/CommentTask";
import { Helmet } from "react-helmet";
import { fTime } from "../../../../../utils/formatTime";
import EditTaskDescriptionModal from "../../../EditTaskDescriptionModal/EditTaskDescriptionModal";
import EditTaskAttachmentModal from "../../../EditTaskAttachmentModal/EditTaskAttachmentModal";
import FileImage from "../CommentTask/components/FileImage";
import ChildrenTask from "../ChildrenTask/ChildrenTask";
import {
  KeyboardArrowDownRounded,
  KeyboardArrowLeftRounded,
  KeyboardArrowUpRounded
} from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { useTranslation } from "react-i18next";

dayjs.extend(duration);

function RowIconLayout(props: {
  icon: React.ReactElement;
  content: React.ReactElement;
}) {
  return (
    <Box display="flex" margin="6px 0">
      {props.icon}
      <Box alignContent={"center"} style={{ marginLeft: "8px" }}>
        {props.content}
      </Box>
    </Box>
  );
}

interface IProps {
  id?: string;
  onJobUpdated?: () => void;
  onClose?: () => void;
}

/**
 * TaskDetail Component - Displays detailed information about a specific task
 *
 * @component
 * @param {IProps} props - Component props
 * @param {string} props.id - Optional task ID
 * @param {Function} props.onJobUpdated - Callback function when job/task is updated
 *
 * @description
 * This component provides a detailed view of a task, including:
 * - Task title and breadcrumb navigation
 * - Task status management (mark as failed, move to next stage)
 * - Task description with rich text support
 * - Deadline and SLA tracking
 * - Stage progress visualization
 * - File attachments management
 * - Sub-tasks management
 * - Comments section
 * - Task progress tracking
 *
 * The component supports various actions like:
 * - Updating task description
 * - Managing file attachments
 * - Creating sub-tasks
 * - Moving task to next stage
 * - Marking task as failed
 *
 * @features
 * - Real-time remaining time calculation
 * - Dynamic stage filtering based on current stage type
 * - Expandable/collapsible description
 * - Permission-based action controls
 * - File attachment preview
 * - Progress tracking
 *
 * @dependencies
 * - Material-UI components
 * - dayjs for date handling
 * - Custom hooks (useAxios, useNavigateSearch)
 * - Custom services (TaskService)
 *
 * @returns {JSX.Element} Rendered TaskDetail component
 */
const TaskDetail = ({ id, onJobUpdated: onStatusUpdated, onClose }: IProps) => {
  const navigateSearch = useNavigateSearch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [forceUpdateSubTasks, setForce] = useState("");
  const deleteTaskAxios = useAxios<string>({ loading: "OnRequest" });
  const getTaskInfo = useAxios<ITask>({ loading: "OnRequest" });
  const markAsFailedAxios = useAxios<string>({ loading: "OnRequest" });
  const nextStageAxios = useAxios<string>({ loading: "OnRequest" });
  const reloadChannel = useMemo(() => new BroadcastChannel("reload-task"), []);

  const [remainingTime, setRemainingTime] = useState<string | null>(null);
  const [openFailureDialog, setOpenFailureDialog] = useState(false);
  const [openNextStageDialog, setOpenNextStageDialog] = useState(false);
  const [failureReason, setFailureReason] = useState("");
  const [openCreateSubTask, setOpenCreateSubTask] = useState(false);
  const [openUpdateDescription, setOpenUpdateDescription] = useState(false);
  const [openUpdateAttachment, setOpenUpdateAttachment] = useState(false);

  const [taskReloadTime, setTaskReloadTime] = useState<number | undefined>();
  const { t } = useTranslation();

  const taskId = useMemo(() => id ?? searchParams.get("id"), [searchParams]);
  const isHasPermission = useMemo(() => {
    if (!getTaskInfo.data) return false;
    return (
      getTaskInfo.data.roleBoardByUser.isCreator ||
      getTaskInfo.data.roleBoardByUser.isProcessManagement ||
      getTaskInfo.data.roleBoardByUser.isAssignTask
    );
  }, [getTaskInfo.data]);

  useEffect(() => {
    getTask(taskId);
  }, [taskId]);

  const reloadCallback = (event: MessageEvent<any>) => {
    setTaskReloadTime(event.timeStamp);
  };

  useEffect(() => {
    reloadChannel.addEventListener("message", reloadCallback);
    return () => {
      reloadChannel.removeEventListener("message", reloadCallback);
      reloadChannel.close();
    };
  }, [reloadChannel]);

  useEffect(() => {
    getTask(taskId);
  }, [taskReloadTime]);

  // Filter stages based on the current stage type
  const filteredStages = useMemo(() => {
    if (!getTaskInfo.data) return [];
    const currentStageIndex = getTaskInfo.data.stages.findIndex(
      e => e.name === getTaskInfo.data!.info.currentStage
    );

    return getTaskInfo.data.stages.reduce((acc, stage, index) => {
      const currentStage = getTaskInfo.data!.stages[currentStageIndex];
      const currentStageType = currentStage?.type;

      if (currentStageType !== EStageType.Step) {
        // If current stage is not a step, include all steps and the current stage
        if (stage.type === EStageType.Step || stage.id === currentStage?.id) {
          acc.push(stage);
        }
      } else {
        // If current stage is a step, include all steps and one non-step stage
        if (stage.type === EStageType.Step) {
          acc.push(stage);
        } else if (acc.every(s => s.type === EStageType.Step)) {
          // Add one non-step stage if we haven't added any yet
          acc.push(stage);
        }
      }

      return acc;
    }, [] as IStageHistory[]);
  }, [getTaskInfo.data]);

  const sla = useMemo(() => {
    const stageExpectedTime = filteredStages.find(
      e => e.id === getTaskInfo.data?.taskCard.workflowId
    )?.histories?.expectedTime;

    if (stageExpectedTime) {
      return fTime(stageExpectedTime);
    }
    return null;
  }, [filteredStages]);

  useEffect(() => {
    if (getTaskInfo.data && getTaskInfo.data.taskCard.deadLine) {
      const updateRemainingTime = () => {
        const now = dayjs();
        const deadline = dayjs(getTaskInfo.data!.taskCard.deadLine);
        if (deadline.isBefore(now)) {
          setRemainingTime(null);
        } else {
          const diff = deadline.diff(now);
          const duration = dayjs.duration(diff);
          const totalHours = Math.floor(duration.asHours());
          const minutes = duration.minutes().toString().padStart(2, "0");
          const seconds = duration.seconds().toString().padStart(2, "0");
          setRemainingTime(`${totalHours}:${minutes}:${seconds}`);
        }
      };

      updateRemainingTime();
      const timer = setInterval(updateRemainingTime, 1000);

      return () => clearInterval(timer);
    }
  }, [getTaskInfo.data]);

  useEffect(() => {
    if (getTaskInfo.error)
      toggleMessage({
        type: "error",
        message: getTaskInfo.error.message ?? ""
      });
  }, [getTaskInfo.error]);

  useEffect(() => {
    if (deleteTaskAxios.isSuccess)
      navigateSearch(ROUTE_PATH.PROJECTS, {
        id: getTaskInfo.data!.taskCard.boardId as string
      });
  }, [deleteTaskAxios.isSuccess]);

  useEffect(() => {
    if (markAsFailedAxios.isSuccess) {
      onStatusUpdated?.();
      toggleMessage({
        type: "success",
        message: markAsFailedAxios.message ?? t("Management.TaskMarkedAsFailed")
      });
      handleCloseFailureDialog();
      getTask(taskId);
      navigateSearch(ROUTE_PATH.PROJECTS, {
        id: getTaskInfo.data!.taskCard.boardId as string
      }); // Back to board
    }
    if (markAsFailedAxios.error) {
      toggleMessage({
        type: "error",
        message:
          markAsFailedAxios.error.message ?? t("Management.UnexpectedError")
      });
    }
  }, [markAsFailedAxios.isSuccess, markAsFailedAxios.error]);

  useEffect(() => {
    if (nextStageAxios.isSuccess) {
      onStatusUpdated?.();
      toggleMessage({
        type: "success",
        message:
          nextStageAxios.message ?? t("Management.TaskMovedToTheNextStage")
      });
      handleCloseNextStageDialog();
      getTask(taskId);
    }
    if (nextStageAxios.error) {
      toggleMessage({
        type: "error",
        message:
          nextStageAxios.error.message ??
          t("Management.TaskMovedToTheNextStage")
      });
    }
  }, [nextStageAxios.isSuccess, nextStageAxios.error]);

  const getTask = async (id: string | null) => {
    if (id) getTaskInfo.request(TaskService.getSingle(id));
  };

  const deleteTask = async () => {
    if (getTaskInfo.data!)
      deleteTaskAxios.request(
        TaskService.deleteTask(getTaskInfo.data!.info.id)
      );
  };

  const onUpdateTrackersSuccess = () => {
    onStatusUpdated?.();
    getTask(taskId);
  };

  const onUpdateAttachmentSuccess = () => {
    onStatusUpdated?.();
    getTask(taskId);
  };

  const onUpdateDescriptionSuccess = () => {
    onStatusUpdated?.();
    getTask(taskId);
  };

  const handleOpenFailureDialog = () => {
    setOpenFailureDialog(true);
  };

  const handleCloseFailureDialog = () => {
    setOpenFailureDialog(false);
    setFailureReason("");
  };

  const handleOpenNextStageDialog = () => {
    setOpenNextStageDialog(true);
  };

  const handleCloseNextStageDialog = () => {
    setOpenNextStageDialog(false);
  };

  const handleMarkAsFailed = () => {
    if (getTaskInfo.data && failureReason.trim()) {
      markAsFailedAxios.request(
        TaskService.markAsFailed(getTaskInfo.data.taskCard.id, failureReason)
      );
    } else {
      toggleMessage({
        type: "error",
        message: t("Management.PleaseEnterTheReasonForFailure")
      });
    }
  };

  const handleNextStage = () => {
    if (getTaskInfo.data) {
      nextStageAxios.request(TaskService.nextStage(getTaskInfo.data.info.id));
    }
  };

  const formatVND = (value: number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND"
    }).format(value);
  };

  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isFullDescription, setIsFullDescription] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<any>(null);
  const maxHeight = 200;
  const checkOverflow = () => {
    if (containerRef.current) {
      setIsOverflowing(containerRef.current.clientHeight === maxHeight);
    }
  };

  useEffect(() => {
    checkOverflow();
  }, [getTaskInfo.data?.taskCard.description]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          // backgroundColor: "#267CDE",
          padding: "8px 16px"
        }}
      >
        <Typography
          fontSize={16}
          fontWeight={500}
          color={variableStyles.NaturalColor300}
        >
          <Breadcrumbs
            separator="›"
            aria-label="breadcrumb"
            sx={{ fontSize: 16, color: "#000000" }}
          >
            <Typography
              variant="overline"
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "unset",
                //color: "#ffffff",
                "&:hover": {
                  textDecoration: "underline #ffffff",
                  cursor: "pointer"
                }
              }}
              onClick={() =>
                navigateSearch(ROUTE_PATH.PROJECTS, {
                  id: getTaskInfo.data!.taskCard.boardId
                })
              }
            >
              {getTaskInfo.data?.info.boardName}
            </Typography>
            ,
            <Typography
              variant="overline"
              fontSize={"11px"}
              color={"#1C5750"}
              fontWeight={600}
              letterSpacing={"unset"}
            >
              {getTaskInfo.data?.info.currentStage}
            </Typography>
          </Breadcrumbs>
        </Typography>
        <CloseRoundedIcon
          onClick={onClose}
          sx={{ color: "black", height: 24, width: 24 }}
        />
      </Box>

      <Wrapper
        active={
          (getTaskInfo.data === undefined && getTaskInfo.isLoading) ||
          deleteTaskAxios.isLoading ||
          markAsFailedAxios.isLoading ||
          nextStageAxios.isLoading
        }
        spinner
      >
        {getTaskInfo.data && (
          <Helmet>
            <meta charSet="utf-8" />
            <title>{getTaskInfo.data.taskCard.title ?? "Nhiệm vụ"}</title>
          </Helmet>
        )}
        {getTaskInfo.data === undefined && getTaskInfo.isLoading && (
          <Box sx={{ height: "500px", width: "100%" }}></Box>
        )}

        {getTaskInfo.data && (
          <Box className="content-container" sx={{ position: "relative" }}>
            {/* <IconButton
              size="small"
              style={{
                width: "28px",
                height: "28px",
                position: "absolute",
                right: "-0.7%",
                top: "10%",
                backgroundColor: "#000000",
                boxShadow: "1px 1px 10px 1px grey",
                transform: `rotate(${isExpanded ? 180 : 0}deg)`,
                cursor: "pointer"
              }}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <KeyboardArrowLeftRounded sx={{ color: "#000000" }} />
            </IconButton> */}
            <Box className="content-header">
              {!id && (
                <ArrowBackIcon
                  onClick={() => navigate(-1)}
                  sx={{ color: variableStyles.greyA100, fontSize: "20px" }}
                />
              )}
              <Box className="header-title">
                <Typography color="black" fontSize={18} fontWeight={700}>
                  {getTaskInfo.data.taskCard.title}
                </Typography>
              </Box>

              {![ETaskStatus.Failed, ETaskStatus.Complete].includes(
                getTaskInfo.data.taskCard.status ?? ETaskStatus.Inprogress
              ) && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#53b727",
                    height: 32,
                    fontSize: 12,
                    boxShadow: "unset"
                  }}
                  startIcon={<PlayArrowRoundedIcon />}
                  onClick={
                    !nextStageAxios.isLoading
                      ? () => handleOpenNextStageDialog()
                      : undefined
                  }
                >
                  {t("Management.Transition")}
                </Button>
              )}

              {getTaskInfo.data.taskCard.status !== ETaskStatus.Failed && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#f8ecec",
                    color: "#be495d",
                    margin: "0 10px",
                    height: 32,
                    fontSize: 12,
                    boxShadow: "unset"
                  }}
                  onClick={
                    !markAsFailedAxios.isLoading
                      ? () => handleOpenFailureDialog()
                      : undefined
                  }
                >
                  {t("Management.MarkAsFailed")}
                </Button>
              )}

              {/* <BasicMenu
              onChange={() => {}}
              label={
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    minWidth: "unset",
                    color: "#878787",
                    bgcolor: "#f3f3f3",
                    height: 32,
                    width: 32,
                    boxShadow: "unset"
                  }}
                >
                  <KeyboardArrowDownRoundedIcon
                    sx={{ height: 18, width: 18 }}
                  />
                </Button>
              }
              padding={0}
              options={[
                {
                  value: "delete",
                  label: (
                    <Box
                      sx={{
                        width: "100%",
                        padding: "11px 12px",
                        color: "#ff4e4e"
                      }}
                      onClick={() => deleteTask()}
                    >
                      Xoá
                    </Box>
                  )
                }
              ]}
            /> */}
            </Box>
            <Divider
              sx={{ margin: "16px -15px", borderColor: "#f0f0f0" }}
            ></Divider>

            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <Box>
                <div className="content-row">
                  <Typography sx={{ fontSize: 12 }}>
                    {getTaskInfo.data.taskCard.personAssigned && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between"
                        }}
                      >
                        {/* <Typography fontSize={18} color="black" fontWeight={400}>
                  {getTaskInfo.data.taskCard.title}
                </Typography> */}
                        <Box display="flex" alignItems={"center"}>
                          <Box flexGrow={3}>
                            <Avatar
                              src={
                                getTaskInfo.data.taskCard.personAssigned.avatar
                              }
                              alt="Avatar-Image"
                              width={20}
                              height={20}
                            />
                          </Box>
                          <Typography
                            fontSize={12}
                            //textOverflow={"ellipsis"}
                            // whiteSpace={"nowrap"}
                            // overflow={"hidden"}
                            //maxWidth={"100px"}
                            paddingLeft={1}
                          >
                            {getTaskInfo.data.taskCard.personAssigned.fullName}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Typography>
                </div>
                <div className="content-row">
                  <RowIconLayout
                    content={
                      <div
                        style={{
                          color: "#858585",
                          lineHeight: 1,
                          fontSize: 12
                        }}
                      >
                        {getTaskInfo.data.taskCard.description ? (
                          <p>
                            {(() => {
                              const parser = new DOMParser();
                              const doc = parser.parseFromString(
                                getTaskInfo.data.taskCard.description,
                                "text/html"
                              );
                              const firstTextNode =
                                doc.body.querySelector("p, div, span");
                              if (firstTextNode) {
                                const text = firstTextNode.textContent || "";
                                return (
                                  text.split(" ").slice(0, 20).join(" ") +
                                  (text.split(" ").length > 20 ? "..." : "")
                                );
                              }
                              return t("Common.NoDescription");
                            })()}
                          </p>
                        ) : (
                          t("Common.NoDescription")
                        )}
                      </div>
                    }
                    icon={
                      <BookmarkRoundedIcon
                        sx={{ color: variableStyles.greyA100, fontSize: 16 }}
                      />
                    }
                  />
                </div>
                <div className="content-row">
                  <RowIconLayout
                    content={
                      <Box display="flex">
                        <Typography fontSize={12} color={"#858585"}>
                          {t("Management.StageDueDate")}:
                        </Typography>
                        <Typography sx={{ margin: "0 4px", fontSize: 12 }}>
                          {getTaskInfo.data.taskCard.deadLine
                            ? dayjs(getTaskInfo.data.taskCard.deadLine).format(
                                "HH:mm DD/MM/YYYY"
                              )
                            : t("Management.NoDeadline")}
                        </Typography>
                        <Typography color={"#858585"} fontSize={12}>
                          - SLA:
                        </Typography>
                        <Typography sx={{ margin: "0 4px" }} fontSize={12}>
                          {sla
                            ? `${sla.hours ? `${sla.hours}h` : ""}${sla.minutes ? ` ${sla.minutes}m` : ""}`
                            : "0m"}
                        </Typography>
                        {remainingTime && (
                          <Typography color={"#858585"} fontSize={12}>
                            {t("Management.Remaining")}:
                            <Box
                              component="span"
                              fontWeight="fontWeightMedium"
                              color={variableStyles.GreenPrimaryColor400}
                            >
                              {" "}
                              {remainingTime}
                            </Box>
                          </Typography>
                        )}
                      </Box>
                    }
                    icon={
                      <ErrorRoundedIcon
                        sx={{ color: variableStyles.greyA100, fontSize: 16 }}
                      />
                    }
                  />
                </div>

                {getTaskInfo.data.taskCard.price && (
                  <div className="content-row">
                    <RowIconLayout
                      content={
                        <Box display="flex">
                          <Typography fontSize={12} color={"#858585"}>
                            {t("Management.Price")}:
                          </Typography>
                          <Typography sx={{ margin: "0 4px", fontSize: 12 }}>
                            {formatVND(getTaskInfo.data.taskCard.price)}
                          </Typography>
                        </Box>
                      }
                      icon={
                        <AttachMoneyIcon
                          sx={{ color: variableStyles.greyA100, fontSize: 16 }}
                        />
                      }
                    />
                  </div>
                )}

                {getTaskInfo.data.taskCard.deposit && (
                  <div className="content-row">
                    <RowIconLayout
                      content={
                        <Box display="flex">
                          <Typography fontSize={12} color={"#858585"}>
                            {t("Management.Deposit")}:
                          </Typography>
                          <Typography sx={{ margin: "0 4px", fontSize: 12 }}>
                            {formatVND(getTaskInfo.data.taskCard.deposit)}
                          </Typography>
                        </Box>
                      }
                      icon={
                        <AttachMoneyIcon
                          sx={{ color: variableStyles.greyA100, fontSize: 16 }}
                        />
                      }
                    />
                  </div>
                )}

                {/* <div className="content-row">
                <RowIconLayout
                  content={<Tag name="backend" />}
                  icon={
                    <SellRoundedIcon
                      sx={{ color: variableStyles.greyA100, width: "16px" }}
                    />
                  }
                />
              </div> */}
              </Box>
              {getTaskInfo.data.taskCard.deadLine && (
                <Box display="flex" flexDirection="column" maxWidth={120}>
                  <Divider
                    sx={{ borderStyle: "dashed", margin: "10px 0" }}
                  ></Divider>

                  <Typography fontSize={12} color={"#858585"} textAlign={"end"}>
                    Deadline:
                    <Box
                      component="span"
                      fontWeight="fontWeightRegular"
                      fontSize={12}
                      color={variableStyles.colorBlack}
                    >
                      {dayjs(getTaskInfo.data.taskCard.deadLine).format(
                        " HH:mm "
                      )}
                    </Box>
                    <Box
                      component="span"
                      fontSize={12}
                      fontWeight="fontWeightRegular"
                      color={"#E0868C"}
                    >
                      {dayjs(getTaskInfo.data.taskCard.deadLine)
                        .locale("vi")
                        .format("dddd, DD/MM/YYYY")}
                    </Box>
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                margin: "16px 0",
                width: "100%"
              }}
            >
              <StageProgress
                currentStage={getTaskInfo.data.info.currentStage}
                stages={filteredStages}
              />
            </Box>
            <ContentLayout
              title={t("Common.Description")}
              action={
                isHasPermission ? (
                  <Box>
                    <Button
                      variant="text"
                      sx={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        marginLeft: "16px",
                        color: variableStyles.Info500
                      }}
                      onClick={() => setOpenUpdateDescription(true)}
                    >
                      <Typography fontSize={12} lineHeight={1.5}>
                        {t("Common.Edit")}
                      </Typography>
                    </Button>
                  </Box>
                ) : null
              }
              children={
                <Card
                  sx={{
                    marginTop: "10px",
                    borderRadius: 1.5,
                    background: "#f9f9f9",
                    boxShadow: "unset",
                    padding: "8px 16px 8px"
                  }}
                >
                  <Box sx={{ paddingBottom: "8px" }}>
                    {getTaskInfo.data.taskCard.description ? (
                      <div>
                        <div
                          ref={containerRef}
                          style={{
                            maxHeight: isFullDescription
                              ? "none"
                              : `${maxHeight}px`,
                            overflow: "hidden",
                            transition: "max-height 0.3s ease"
                          }}
                          dangerouslySetInnerHTML={{
                            __html: getTaskInfo.data.taskCard.description
                          }}
                        />
                        {isOverflowing && (
                          <Button
                            endIcon={
                              isFullDescription ? (
                                <KeyboardArrowUpRounded />
                              ) : (
                                <KeyboardArrowDownRounded />
                              )
                            }
                            color="info"
                            sx={{ justifyItems: "center", margin: "14px 0" }}
                            onClick={() =>
                              setIsFullDescription(!isFullDescription)
                            }
                          >
                            {isFullDescription
                              ? t("Common.CollapseDescription")
                              : t("Common.ExpandDescription")}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div>{t("Common.NoDescription")}</div>
                    )}
                  </Box>
                </Card>
              }
            />

            <ContentLayout
              title={t("Management.Attachments")}
              action={
                isHasPermission ? (
                  <Box>
                    <Button
                      sx={{ color: variableStyles.Info500 }}
                      onClick={() => setOpenUpdateAttachment(true)}
                    >
                      <Typography fontSize={12} lineHeight={1.5}>
                        {t("Common.UploadFile")}
                      </Typography>
                    </Button>
                  </Box>
                ) : null
              }
              children={
                <Card
                  sx={{
                    marginTop: "10px",
                    borderRadius: 1.5,
                    background: "#f9f9f9",
                    boxShadow: "unset",
                    padding: "8px 16px 8px"
                  }}
                >
                  {getTaskInfo.data.taskCard.attachmentFiles &&
                    getTaskInfo.data.taskCard.attachmentFiles.length > 0 && (
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        color={"#858585"}
                        fontSize={12}
                      >
                        {/* Tài liệu đính kèm: */}
                        {getTaskInfo.data.taskCard.attachmentFiles.map(file => (
                          <Box
                            key={file.id ?? file.path}
                            display={"flex"}
                            padding={0.5}
                            flexDirection={"row"}
                            gap={1}
                            sx={{ cursor: "pointer" }}
                          >
                            <FileImage type={file.extension.toLowerCase()} />
                            <Typography
                              color="#3091F3"
                              variant="subtitle2"
                              onClick={() => {
                                window.open(
                                  ROUTE_PATH.RESOURCE +
                                    `?${createSearchParams({ type: file.extension, name: file.fileName, path: file.path })}`,
                                  "_blank"
                                );
                              }}
                            >
                              {file.fileName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Card>
              }
            />

            {/* <ContentLayout
            title={"Trường tuỳ chỉnh"}
            action={
              <Button sx={{ color: variableStyles.Info500 }}>
                <Typography variant="caption">Xem tất cả</Typography>
              </Button>
            }
          /> */}

            <ContentLayout
              title={t("Common.TaskList")}
              action={
                <Button
                  sx={{ color: variableStyles.Info500 }}
                  onClick={() => setOpenCreateSubTask(true)}
                >
                  <Typography fontSize={12}>
                    {t("Management.CreateTask")}
                  </Typography>{" "}
                </Button>
              }
              children={
                <ChildrenTask
                  TaskCardId={getTaskInfo.data.taskCard.id}
                  BoardId={getTaskInfo.data.taskCard.boardId}
                  isHasPermission={isHasPermission}
                  stages={filteredStages}
                  force={forceUpdateSubTasks}
                />
              }
            />
            {getTaskInfo.data.taskCard.deadlinePersonals.length > 0 && (
              <ContentLayout
                title={t("Management.PersonalDeadlines")}
                children={
                  <>
                    <Typography
                      fontSize={14}
                      color="GrayText"
                      fontWeight="400"
                      marginTop="3px"
                      sx={{
                        marginTop: "10px",
                        borderRadius: 1.5,
                        background: "#f9f9f9",
                        boxShadow: "unset",
                        padding: "8px 16px 8px"
                      }}
                    >
                      <Box>
                        {getTaskInfo.data.taskCard.deadlinePersonals.map(p => {
                          return (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "5px",
                                  marginTop: "5px",
                                  fontSize: 14
                                }}
                              >
                                <Avatar
                                  src={p?.user?.avatar}
                                  alt="Avatar-Image"
                                  width={20}
                                  height={20}
                                />{" "}
                                {t("Management.Deadline")}:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                  {" "}
                                  {dayjs(p.startDate).format("DD/MM/YYYY")}{" "}
                                  {"-"} {dayjs(p.endDate).format("DD/MM/YYYY")}
                                </span>
                              </Box>
                            </>
                          );
                        })}
                      </Box>
                    </Typography>
                  </>
                }
              />
            )}

            <CommentTask taskId={`${getTaskInfo.data.taskCard.id}`} />
          </Box>
        )}
        {/* Right */}
        {getTaskInfo.data && (
          <Box
            className="container-progress"
            display={isExpanded ? "flex" : "none"}
            sx={{
              transition: "all 1s ease-in"
            }}
          >
            <JobDetailProgress
              key={getTaskInfo.data.taskCard.id}
              canUpdateTrackers={isHasPermission}
              task={getTaskInfo.data}
              stagesHistorys={filteredStages}
              onUpdateTrackersSuccess={onUpdateTrackersSuccess}
            />
          </Box>
        )}
        {getTaskInfo.data && (
          <CreateTaskModal
            open={openCreateSubTask}
            taskId={getTaskInfo.data.info.id}
            isUpdate={false}
            hasDocument={false}
            workflowId={getTaskInfo.data.taskCard.boardId}
            onCreateSuccess={newTaskcard => {
              setOpenCreateSubTask(false);
              setForce((newTaskcard?.id as string) ?? "");
            }}
            handleClose={() => setOpenCreateSubTask(false)}
          />
        )}

        <Modal
          title={t("Management.MarkAsFailed")}
          textSubmit={t("Modal.SubmitText")}
          textClose={t("Modal.CancelText")}
          open={openFailureDialog}
          onSubmit={handleMarkAsFailed}
          disabledSubmit={markAsFailedAxios.isLoading}
          onClose={handleCloseFailureDialog}
          width="500px"
        >
          <TextField
            margin="dense"
            id="failureReason"
            required
            type="text"
            fullWidth
            variant="outlined"
            placeholder={t("Management.EnterTheReasonForFailure")}
            value={failureReason}
            onChange={e => setFailureReason(e.target.value)}
          />
        </Modal>
        <Modal
          title={t("Management.Transition")}
          textSubmit={t("Modal.SubmitText")}
          textClose={t("Modal.CancelText")}
          open={openNextStageDialog}
          onSubmit={handleNextStage}
          disabledSubmit={nextStageAxios.isLoading}
          onClose={handleCloseNextStageDialog}
          width="500px"
        >
          {t("Modal.SubmitText")}
        </Modal>
        {getTaskInfo.data && (
          <EditTaskDescriptionModal
            open={openUpdateDescription}
            onClose={() => setOpenUpdateDescription(false)}
            onUpdateSuccess={onUpdateDescriptionSuccess}
            task={getTaskInfo.data.taskCard}
          />
        )}
        {getTaskInfo.data && (
          <EditTaskAttachmentModal
            open={openUpdateAttachment}
            onClose={() => setOpenUpdateAttachment(false)}
            onUpdateSuccess={onUpdateAttachmentSuccess}
            task={getTaskInfo.data.taskCard}
          />
        )}
      </Wrapper>
    </>
  );
};

export default TaskDetail;
